import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Card, CircularProgress, Divider, IconButton,
  MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams } from 'react-router-dom';
import { usePageTitle } from '../../components/shell/AppShell';
import {
  useCreateStickerLabelTemplate, useDeleteStickerLabelTemplate,
  useMyPermissions, useQrBatchDetail, useStickerLabelTemplates,
  scQrBatchPrintUrl,
} from '../../api/queries';
import type { StickerLabelTemplate } from '../../api/types';
import { apiBaseUrl } from '../../auth/config';
import { useAccessToken } from '../../auth/useAccessToken';
import { useAlert, useConfirm, usePrompt } from '../../components/dialogs/DialogProvider';

/**
 * Dedicated print-preview surface for an SC QR pair batch — line-for-line
 * mirror of StickerBatchPrintPage, retargeted at /api/v1/factory/qr-batches.
 *
 *   - Layout controls on the left: pick a saved label template (Avery
 *     standards + user-created customs) or override paper / label /
 *     grid / margins / gutter freely. Reuses the same
 *     platform.sticker_label_template registry as the per-machine
 *     sticker print page so a layout saved in either place is
 *     available in both.
 *   - "Save as new template" captures current values into a custom row.
 *   - Live PDF preview on the right via iframe (browser native viewer).
 *
 * Re-render is on-demand: the user tweaks knobs and clicks "Refresh
 * preview" rather than re-fetching on every keystroke.
 */
export default function ScQrBatchPrintPage() {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const getToken = useAccessToken();

  const batchQ     = useQrBatchDetail(batchId ?? null);
  const templatesQ = useStickerLabelTemplates();
  const permsQ     = useMyPermissions();
  const createTpl  = useCreateStickerLabelTemplate();
  const deleteTpl  = useDeleteStickerLabelTemplate();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const alert  = useAlert();

  const batch = batchQ.data;
  usePageTitle(batch ? `Print ${batch.code}` : 'Print preview');
  const canManageTemplates = !!permsQ.data?.codes.includes('platform.stickers.label_templates');

  const [templateId, setTemplateId] = useState<string>('');
  const [paper, setPaper]           = useState<string>('');
  const [widthMm, setWidthMm]       = useState<number>(0);
  const [heightMm, setHeightMm]     = useState<number>(0);
  const [cols, setCols]             = useState<number>(0);
  const [rows, setRows]             = useState<number>(0);
  const [marginLeft, setMarginLeft]     = useState<number>(5);
  const [marginRight, setMarginRight]   = useState<number>(5);
  const [marginTop, setMarginTop]       = useState<number>(5);
  const [marginBottom, setMarginBottom] = useState<number>(5);
  const [gutter, setGutter]             = useState<number>(0);

  const [pdfUrl,   setPdfUrl]   = useState<string | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);

  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Initialise form state from the batch's stored layout the first time
  // it loads. Margins/gutter default to the renderer defaults.
  useEffect(() => {
    if (batch && paper === '') {
      setPaper(batch.labelPaper);
      setWidthMm(batch.labelWidthMm);
      setHeightMm(batch.labelHeightMm);
      setCols(batch.colsPerSheet);
      setRows(batch.rowsPerSheet);
    }
  }, [batch, paper]);

  // Auto-load the first preview as soon as the form has values.
  useEffect(() => {
    if (paper && widthMm > 0 && heightMm > 0 && cols > 0 && rows > 0 && pdfUrl === null && !loading && !error) {
      void renderPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paper]);

  // Cleanup the previous blob URL when navigating away or re-rendering.
  useEffect(() => {
    return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
  }, [pdfUrl]);

  function applyTemplate(t: StickerLabelTemplate) {
    setTemplateId(t.id);
    setPaper(t.paper);
    setWidthMm(t.labelWidthMm);
    setHeightMm(t.labelHeightMm);
    setCols(t.colsPerSheet);
    setRows(t.rowsPerSheet);
    setMarginLeft(t.marginLeftMm);
    setMarginRight(t.marginRightMm);
    setMarginTop(t.marginTopMm);
    setMarginBottom(t.marginBottomMm);
    setGutter(t.gutterMm);
  }

  async function saveAsNewTemplate() {
    const defaultName = paper && widthMm && heightMm
      ? `Custom ${widthMm}×${heightMm}mm ${cols}×${rows} on ${paper}`
      : 'Custom layout';
    const raw = await prompt({
      title: 'Save layout as a template',
      message: 'Templates appear in the dropdown above so the same paper / grid / margins can be re-used on future SC QR batches.',
      placeholder: 'Template name',
      defaultValue: defaultName,
      confirmLabel: 'Save template',
    });
    if (raw === null) return;
    const name = raw.trim();
    if (!name) return;
    try {
      const created = await createTpl.mutateAsync({
        name, paper,
        labelWidthMm: widthMm, labelHeightMm: heightMm,
        colsPerSheet: cols, rowsPerSheet: rows,
        marginLeftMm: marginLeft, marginRightMm: marginRight,
        marginTopMm: marginTop, marginBottomMm: marginBottom,
        gutterMm: gutter,
        notes: null,
      });
      setTemplateId(created.id);
    } catch (err) {
      await alert({ title: 'Save failed', message: (err as Error).message, severity: 'error' });
    }
  }

  async function deleteCustomTemplate() {
    if (!templateId) return;
    const t = templatesQ.data?.find((x) => x.id === templateId);
    if (!t || t.isStandard) return;
    const ok = await confirm({
      title: 'Delete custom template',
      message: <>Delete custom template <strong>{t.name}</strong>? Existing batches that already snapshot it stay unchanged.</>,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteTpl.mutateAsync(templateId);
      setTemplateId('');
    } catch (err) {
      await alert({ title: 'Delete failed', message: (err as Error).message, severity: 'error' });
    }
  }

  async function renderPreview() {
    if (!batchId) return;
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const url = apiBaseUrl + scQrBatchPrintUrl(batchId, {
        paper, widthMm, heightMm, cols, rows,
        marginLeftMm: marginLeft,
        marginRightMm: marginRight,
        marginTopMm: marginTop,
        marginBottomMm: marginBottom,
        gutterMm: gutter,
      });
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        let detail = '';
        try { detail = (await resp.json()).message ?? ''; } catch { /* not JSON */ }
        setError(`Render failed: HTTP ${resp.status}${detail ? ` — ${detail}` : ''}`);
        return;
      }
      const blob = await resp.blob();
      const next = URL.createObjectURL(blob);
      setPdfUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return next;
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function triggerPrint() {
    const win = iframeRef.current?.contentWindow;
    if (!win) {
      await alert({
        title: 'Preview not ready',
        message: 'The print preview is still rendering — wait a moment and try again.',
        severity: 'info',
      });
      return;
    }
    win.focus();
    win.print();
  }

  const downloadFilename = useMemo(
    () => batch ? `sc-qr-batch-${batch.code}.pdf` : 'sc-qr-pairs.pdf',
    [batch],
  );

  const standards = (templatesQ.data ?? []).filter((t) => t.isStandard);
  const customs   = (templatesQ.data ?? []).filter((t) => !t.isStandard);
  const selectedTemplate = templatesQ.data?.find((t) => t.id === templateId);

  // Each pair = DEVICE + PACKAGING, so the sheet count is `pairs × 2`.
  const labelsPerSheet = cols * rows;
  const totalLabels    = (batch?.generatedCount ?? 0) * 2;
  const sheetEstimate  = labelsPerSheet > 0 ? Math.ceil(totalLabels / labelsPerSheet) : 0;

  return (
    <>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button onClick={() => navigate(`/factory/qr-batches/${batchId}`)} startIcon={<ArrowBackIcon />}>
            Back to batch
          </Button>
          <Box sx={{ flex: 1 }} />
          {pdfUrl && (
            <>
              <Button variant="outlined" startIcon={<DownloadIcon />}
                component="a" href={pdfUrl} download={downloadFilename}>
                Download PDF
              </Button>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={triggerPrint}>
                Print
              </Button>
            </>
          )}
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flex: 1, minHeight: '70vh' }}>
          {/* Layout controls */}
          <Card variant="outlined" sx={{ p: 2, width: { xs: '100%', md: 360 }, flexShrink: 0 }}>
            <Typography variant="overline" color="text.secondary">Label template</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Pick a saved layout (shared with the per-machine sticker
              print page), or tweak any field below and click "Save as
              new" to remember it.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField select label="Template" value={templateId} size="small" sx={{ flex: 1 }}
                onChange={(e) => {
                  const t = templatesQ.data?.find((x) => x.id === e.target.value);
                  if (t) applyTemplate(t);
                  else setTemplateId('');
                }}>
                <MenuItem value=""><em>— Custom (form values below) —</em></MenuItem>
                {standards.length > 0 && (
                  <MenuItem disabled value="__standards__">
                    <em>Standard (Avery)</em>
                  </MenuItem>
                )}
                {standards.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
                {customs.length > 0 && (
                  <MenuItem disabled value="__customs__">
                    <em>Custom</em>
                  </MenuItem>
                )}
                {customs.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </TextField>
              {canManageTemplates && (
                <Tooltip title="Save current values as new template">
                  <span>
                    <IconButton color="primary" onClick={saveAsNewTemplate}
                      disabled={createTpl.isPending || !paper || !widthMm || !heightMm || !cols || !rows}>
                      <SaveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {canManageTemplates && selectedTemplate && !selectedTemplate.isStandard && (
                <Tooltip title={`Delete custom template "${selectedTemplate.name}"`}>
                  <span>
                    <IconButton color="error" onClick={deleteCustomTemplate}
                      disabled={deleteTpl.isPending}>
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography variant="overline" color="text.secondary">Layout</Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField select label="Paper" value={paper} size="small"
                onChange={(e) => { setPaper(e.target.value); setTemplateId(''); }}>
                {['A4', 'A5', 'A6', 'Letter'].map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
              <Stack direction="row" spacing={1}>
                <TextField label="Width (mm)" type="number" value={widthMm} size="small"
                  onChange={(e) => { setWidthMm(parseFloat(e.target.value)); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
                <TextField label="Height (mm)" type="number" value={heightMm} size="small"
                  onChange={(e) => { setHeightMm(parseFloat(e.target.value)); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Cols / sheet" type="number" value={cols} size="small"
                  onChange={(e) => { setCols(parseInt(e.target.value || '0', 10)); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
                <TextField label="Rows / sheet" type="number" value={rows} size="small"
                  onChange={(e) => { setRows(parseInt(e.target.value || '0', 10)); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {labelsPerSheet} per sheet
                {batch && ` → ${sheetEstimate} sheet(s) for ${batch.generatedCount} pairs (${totalLabels} labels — DEVICE + PACKAGING)`}
              </Typography>

              <Typography variant="overline" color="text.secondary" sx={{ mt: 1 }}>Margins (mm)</Typography>
              <Stack direction="row" spacing={1}>
                <TextField label="Left" type="number" value={marginLeft} size="small"
                  onChange={(e) => { setMarginLeft(parseFloat(e.target.value || '0')); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
                <TextField label="Right" type="number" value={marginRight} size="small"
                  onChange={(e) => { setMarginRight(parseFloat(e.target.value || '0')); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField label="Top" type="number" value={marginTop} size="small"
                  onChange={(e) => { setMarginTop(parseFloat(e.target.value || '0')); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
                <TextField label="Bottom" type="number" value={marginBottom} size="small"
                  onChange={(e) => { setMarginBottom(parseFloat(e.target.value || '0')); setTemplateId(''); }}
                  sx={{ flex: 1 }} />
              </Stack>
              <TextField label="Gutter (between labels, mm)" type="number" value={gutter} size="small"
                onChange={(e) => { setGutter(parseFloat(e.target.value || '0')); setTemplateId(''); }} />

              <Button variant="contained" startIcon={<RefreshIcon />}
                disabled={loading || !cols || !rows || !widthMm || !heightMm}
                onClick={renderPreview}>
                {loading ? 'Rendering…' : 'Refresh preview'}
              </Button>
              {error && <ErrorAlert>{error}</ErrorAlert>}
            </Stack>
          </Card>

          {/* PDF preview */}
          <Card variant="outlined" sx={{ flex: 1, p: 0, position: 'relative', minHeight: 600 }}>
            {pdfUrl ? (
              <iframe
                ref={iframeRef}
                src={pdfUrl}
                title="QR pair preview"
                style={{ width: '100%', height: '100%', border: 0 }}
              />
            ) : loading ? (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex',
                         alignItems: 'center', justifyContent: 'center' }}>
                <Stack alignItems="center" spacing={1}>
                  <CircularProgress />
                  <Typography variant="body2" color="text.secondary">Rendering PDF…</Typography>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex',
                         alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Click "Refresh preview" on the left to render.
                </Typography>
              </Box>
            )}
          </Card>
        </Stack>
      </Stack>
    </>
  );
}
